import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { isApiError } from "../api/errors";

export interface MessageReadMutation {
  mutateAsync(messageIds: string[]): Promise<unknown>;
  reset(): void;
}

type ReadBoundaryState = {
  confirmed: Set<string>;
  stale: Set<string>;
  pending: Map<string, string[]>;
  failed: string | null;
  running: boolean;
};

function newReadBoundaryState(): ReadBoundaryState {
  return {
    confirmed: new Set(),
    stale: new Set(),
    pending: new Map(),
    failed: null,
    running: false,
  };
}

/**
 * Submit exact server-provided sets after render. The caller supplies transport
 * and a state key (actor-qualified where an account can change in place).
 */
export function useMessageReadBoundary<T extends MessageReadMutation>(
  markRead: T,
  conversationId: string,
  readMessageBatches: string[][],
  enabled = true,
  refreshStaleBatch?: (messageIds: string[]) => Promise<void>,
  revokeAuthority?: (error: unknown) => Promise<void>,
) {
  const enabledRef = useRef(enabled);
  useLayoutEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const statesRef = useRef<Map<string, ReadBoundaryState>>(new Map());
  const activeConversationRef = useRef<string | null>(null);
  // A speculative conversation render gets an isolated candidate. It becomes
  // authoritative only in the commit effect below, so an abandoned render can
  // never erase another conversation's retained command authority.
  const state = useMemo(
    () => statesRef.current.get(conversationId) ?? newReadBoundaryState(),
    [conversationId],
  );
  const [drainVersion, setDrainVersion] = useState(0);
  const batches = useMemo(() => {
    const unique = new Map<string, string[]>();
    for (const messageIds of readMessageBatches) {
      if (messageIds.length === 0) continue;
      const signature = JSON.stringify(messageIds);
      if (!unique.has(signature)) unique.set(signature, messageIds);
    }
    return [...unique].map(([signature, messageIds]) => ({
      signature,
      messageIds,
    }));
  }, [readMessageBatches]);
  const aggregateSignature = batches
    .map(({ signature }) => signature)
    .join("|");
  const mutateAsync = markRead.mutateAsync;
  const resetMutation = markRead.reset;

  useLayoutEffect(() => {
    if (!statesRef.current.has(conversationId)) {
      statesRef.current.set(conversationId, state);
    }
    activeConversationRef.current = conversationId;
    resetMutation();
    setDrainVersion((version) => version + 1);
    return () => {
      if (activeConversationRef.current === conversationId) {
        activeConversationRef.current = null;
      }
    };
  }, [conversationId, resetMutation, state]);

  useEffect(() => {
    if (
      activeConversationRef.current !== conversationId ||
      statesRef.current.get(conversationId) !== state
    ) {
      return;
    }
    if (!enabled) return;
    // An observed batch becomes command authority only after this render
    // commits. Retain it independently of later page rotation until success.
    for (const batch of batches) {
      if (
        !state.confirmed.has(batch.signature) &&
        !state.stale.has(batch.signature) &&
        !state.pending.has(batch.signature)
      ) {
        state.pending.set(batch.signature, batch.messageIds);
      }
    }
    if (state.pending.size === 0 || state.running || state.failed) {
      return;
    }
    const pending = [...state.pending].map(([signature, messageIds]) => ({
      signature,
      messageIds,
    }));

    state.running = true;
    void (async () => {
      try {
        for (const batch of pending) {
          if (
            !mountedRef.current ||
            !enabledRef.current ||
            activeConversationRef.current !== conversationId
          )
            return;
          try {
            await mutateAsync(batch.messageIds);
          } catch (error) {
            if (statesRef.current.get(conversationId) === state) {
              if (
                revokeAuthority &&
                isApiError(error) &&
                (error.status === 401 ||
                  error.status === 403 ||
                  error.status === 409)
              ) {
                await revokeAuthority(error);
                state.pending.delete(batch.signature);
                state.failed = null;
                return;
              }
              if (isApiError(error) && error.status === 404) {
                // The exact observed command became stale after a legal purge
                // or authority change. When page provenance is available,
                // refresh that exact bounded page before retiring the command;
                // the replacement server batch is then rendered and drained.
                if (refreshStaleBatch) {
                  try {
                    await refreshStaleBatch(batch.messageIds);
                  } catch {
                    state.failed = batch.signature;
                    return;
                  }
                }
                state.pending.delete(batch.signature);
                state.stale.add(batch.signature);
                state.failed = null;
                continue;
              }
              state.failed = batch.signature;
            }
            return;
          }
          if (statesRef.current.get(conversationId) !== state) {
            return;
          }
          state.confirmed.add(batch.signature);
          state.pending.delete(batch.signature);
        }
      } finally {
        if (statesRef.current.get(conversationId) === state) {
          state.running = false;
          if (
            mountedRef.current &&
            activeConversationRef.current === conversationId
          ) {
            setDrainVersion((version) => version + 1);
          }
        }
      }
    })();
  }, [
    aggregateSignature,
    batches,
    conversationId,
    drainVersion,
    enabled,
    mutateAsync,
    refreshStaleBatch,
    revokeAuthority,
    state,
  ]);

  return {
    ...markRead,
    isError: Boolean(state.failed),
    retry: () => {
      if (state.pending.size === 0) return;
      state.failed = null;
      markRead.reset();
      setDrainVersion((version) => version + 1);
    },
  };
}
